import { call, put, takeLatest, select } from 'redux-saga/effects';
import {
    fetchCompanyProfileFailure,
    fetchCompanyProfileStart, fetchCompanyProfileSuccess,
    setCompanyName,
    updateCompanyProfileFailure,
    updateCompanyProfileStart,
    updateCompanyProfileSuccess,
    setCompanySubmitt,
    setApiStatus,
    setShowSuccessPopUp
} from '../slices/sellerProfileSlice';
import { PayloadAction } from '@reduxjs/toolkit';
import axios from "axios";
import { apiGetCall ,apiPutCall} from '../api';
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import {showAPIerror} from '../../pages/SellerProfile/APIs/Helpers';
import i18next from 'i18next';
 
const URL = process.env.REACT_APP_API_BASE;
 
function* checkTokenExpired() {
    // if (jwtDecode(token).exp < Date.now() / 1000) {
    yield (window.instance as any)
        .acquireTokenSilent({
            redirectUri: process.env.REACT_APP_AZURE_REDIRECT,
            authority: process.env.REACT_APP_AZURE_AUTHORITY,
        })
        .then((res) => {
            axios.defaults.headers.common["Authorization"] = `Bearer ${res.idToken}`;
        })
        .catch((e) => {
            if (e instanceof InteractionRequiredAuthError) {
                (window.instance as any).loginRedirect();
            }
        });
 
}
 
function* fetchCompanyProfileSaga(action: PayloadAction<{ seller_id: string, language: string }>) {
    try {
 
        yield call(checkTokenExpired);
        const { seller_id, language } = action.payload;
        const res = yield call(apiGetCall, URL + "profile/" + seller_id);

        // ── Detect API format ──────────────────────────────────────────────────
        // Old format:       { data: { seller: [...], contact: [...] } }
        // New format:       { data: [...] }
        // New format (alt): [...] — array returned directly without wrapper
        // Some environments return the body as a raw JSON string (missing Content-Type header).
        // The API may also emit invalid JSON (missing commas between properties), so we
        // sanitize before parsing: insert commas wherever a value is followed by a new key.
        let parsedRes: any;
        if (typeof res === 'string') {
            const sanitized = res.trim().replace(
                /([\]}"]|true|false|null|-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)\s*\n(\s*")/g,
                '$1,\n$2'
            );
            parsedRes = JSON.parse(sanitized);
        } else {
            parsedRes = res;
        }
        const dataPayload = Array.isArray(parsedRes) ? parsedRes : parsedRes.data;
        if (!dataPayload) {
            throw new Error(`Profile API unexpected shape — res: ${JSON.stringify(parsedRes)}`);
        }
        const isOldFormat = !Array.isArray(dataPayload) && Array.isArray(dataPayload?.seller);

        let rawSellerData: any;
        let finalContactData: any[];

        if (isOldFormat) {
          rawSellerData = dataPayload.seller[0];
          const contacts: any[] = dataPayload.contact || [];
          // Build contact array from the old separate contact list
          finalContactData = contacts;

          // ── Normalize old-format fields to match new-format expectations ────
          const enReverseMap: Record<string, string> = i18next.getResourceBundle('en', 'translation')?.sellerProfile?.dashboard?.statusReverseMap || {};
          const frReverseMap: Record<string, string> = i18next.getResourceBundle('fr', 'translation')?.sellerProfile?.dashboard?.statusReverseMap || {};
          const statusReverseMap: Record<string, string> = { ...enReverseMap, ...frReverseMap };
          const normalizedStatusType =
            statusReverseMap[rawSellerData.status_en] ||
            statusReverseMap[rawSellerData.status_fr] ||
            rawSellerData.status_type ||
            '';

          // POS: extract ISO-2 code from strings like "Australia AU-supported"
          // pos_code (clean "BR,CA" string) takes precedence if pos_preference is absent
          const normalizedPosCode = rawSellerData.pos_code
            ? rawSellerData.pos_code
            : (rawSellerData.pos_preference || '')
                .split(',')
                .map((entry: string) => {
                  const match = entry.trim().match(/\b([A-Z]{2})(?:-\S+)?$/);
                  return match ? match[1] : '';
                })
                .filter(Boolean)
                .join(',');

          // Business model: use language-appropriate label as the code value;
          // the reducer will display it as-is if no code match is found
          const normalizedBizModel = language === 'fr'
            ? (rawSellerData.business_model_type_fr || rawSellerData.business_model_type || '')
            : (rawSellerData.business_model_type || '');

          // Customer type: use language-appropriate label as the customer_type_id;
          // the reducer will display it as-is if no code match is found
          const normalizedCustomerTypeId = language === 'fr'
            ? (rawSellerData.customer_type_fr || rawSellerData.customer_type || '')
            : (rawSellerData.customer_type || '');

          rawSellerData = {
            ...rawSellerData,
            pos_code: normalizedPosCode,
            business_model: normalizedBizModel,
            customer_type_id: normalizedCustomerTypeId,
            status_type: normalizedStatusType,
          };
        } else {
          rawSellerData = dataPayload[0];

          // ── Normalize pos_code / pos_preference ───────────────────────────
          // Handles three formats:
          //   1. Array:  [{id, iata}, ...]          (future / parsed JSON)
          //   2. JSON string: '{"id":"DK","iata":null},{"id":"BR","iata":null}'
          //   3. Plain CSV string: "BR,CA,DK"
          const posRaw = rawSellerData.pos_preference ?? rawSellerData.pos_code ?? '';
          if (Array.isArray(posRaw)) {
            const posIataMap: Record<string, string> = {};
            const posCodes = posRaw
              .map((p: any) => { if (p.iata) posIataMap[p.id] = String(p.iata); return p.id; })
              .join(',');
            rawSellerData = { ...rawSellerData, pos_code: posCodes, pos_iata_map: posIataMap };
          } else if (typeof posRaw === 'string' && posRaw.trim().startsWith('{')) {
            // JSON-object string format from new API
            try {
              const parsed: Array<{ id: string; iata: any }> = JSON.parse(`[${posRaw}]`);
              const posIataMap: Record<string, string> = {};
              const posCodes = parsed
                .map((p) => { if (p.iata != null) posIataMap[p.id] = String(p.iata); return p.id; })
                .join(',');
              rawSellerData = { ...rawSellerData, pos_code: posCodes, pos_iata_map: posIataMap };
            } catch (e) {
              console.warn('Could not parse pos_code JSON string:', posRaw);
            }
          }
          // else: already a plain CSV string — no normalization needed

          // ── Normalize business_model ──────────────────────────────────────
          // Handles two formats:
          //   1. JSON string: '{"id":"CB","iata":null}' or multiple comma-separated
          //   2. Plain CSV string: "CB" or "CB,GDS"
          const bizRaw = rawSellerData.business_model ?? '';
          if (typeof bizRaw === 'string' && bizRaw.trim().startsWith('{')) {
            try {
              const parsed: Array<{ id: string; iata: any }> = JSON.parse(`[${bizRaw}]`);
              rawSellerData = { ...rawSellerData, business_model: parsed.map((p) => p.id).join(',') };
            } catch (e) {
              console.warn('Could not parse business_model JSON string:', bizRaw);
            }
          }

          // Contacts are embedded in the seller object; build a compatible array
          finalContactData = [{
            contact_type: 'admin',
            contact_first_name: rawSellerData.admin_contact_first_name || '',
            contact_last_name: rawSellerData.admin_contact_last_name || '',
            contact_phone: rawSellerData.admin_contact_phone || '',
            contact_email: rawSellerData.admin_contact_email || '',
            contact_id: rawSellerData.admin_contact_id || ''
          }];
        }

        const finalSellerData = rawSellerData;

        yield put(setCompanyName(finalSellerData.company_name));
  
        let defaultDataToReturn = {};
 
        const enCodeMap: Record<string, string> = i18next.getResourceBundle('en', 'translation')?.sellerProfile?.dashboard?.statusCodeMap || {};
        const frCodeMap: Record<string, string> = i18next.getResourceBundle('fr', 'translation')?.sellerProfile?.dashboard?.statusCodeMap || {};

        yield put(fetchCompanyProfileSuccess
            ({
                finalSellerData,
                finalContactData,
                defaultCoData: defaultDataToReturn,
                language,
                enCodeMap,
                frCodeMap,
            }));
 
    } catch (error: any) {
      const {spPageText} = yield select(({ sellerProfile: { spPageText } }) => ({ spPageText }));
            yield put(fetchCompanyProfileFailure(showAPIerror(error, spPageText,setShowSuccessPopUp,setApiStatus, 'SpAPIgetCompanyProfile')));
    }
}
 
function* updateCompanyProfileSaga(action:PayloadAction<any>){
    try{
        yield call(checkTokenExpired);
        const { payload, language } = action.payload;
        const { seller_id } = yield select((state) => state.sellerProfile);

        const response = yield call(apiPutCall, URL + 'profile/' + seller_id, payload);
        if (response.status === 'successful') {
            yield put(updateCompanyProfileSuccess(response));
            yield put(fetchCompanyProfileStart({ seller_id, language }));
            yield put(setCompanySubmitt(true));
        }
    }catch(error:any){
       const {spPageText} = yield select(({ sellerProfile: { spPageText } }) => ({ spPageText }));
       const errResult = showAPIerror(error, spPageText,setShowSuccessPopUp,setApiStatus, 'SpAPIUpdateCompanyProfile');
       yield put(updateCompanyProfileFailure(errResult));
       if (!errResult) {
           const msg = spPageText?.errorGeneral || 'An error occurred. Please try again.';
           yield put(setShowSuccessPopUp({ message: msg, successType: 'error' }));
       }
    }
}
 
export default function* companyProfileSaga() {
    yield takeLatest(fetchCompanyProfileStart.type, fetchCompanyProfileSaga);
    yield takeLatest(updateCompanyProfileStart.type, updateCompanyProfileSaga);
}
 
